import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFilter,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const FILTER_OPTIONS = [
  { value: "upl", label: "Upload Date" },
  { value: "rec", label: "Date Received" },
  { value: "sca", label: "Date Scanned" },
  { value: "pro", label: "Date Processed" },
  { value: "val", label: "Date Validated" },
];

const TABLE_COLUMNS = [
  { key: "provider", label: "Provider", sortable: true },
  { key: "trans_no", label: "TRN#", sortable: true },
  { key: "soa", label: "SOA#", sortable: true },
  { key: "corp_name", label: "Corp", sortable: true },
  { key: "member_name", label: "Member Name", sortable: true },
  { key: "coverage_code", label: "Coverage", sortable: true },
  { key: "provider_name", label: "Provider Name", sortable: true },
  { key: "claim_count", label: "Claim Count", sortable: true },
  { key: "loa_no2", label: "LOA#", sortable: true },
  { key: "batch_no", label: "Batch#", sortable: true },
  { key: "claim_amount", label: "Claim Amt", sortable: true },
  { key: "loa_date_issued", label: "LOA Date Issued", sortable: true },
  { key: "date_received", label: "Date Received", sortable: true },
  { key: "date_scanned_a", label: "Date Scanned", sortable: true },
  { key: "claim_processed_date", label: "Date Processed", sortable: true },
  { key: "processed_by", label: "Processed_By", sortable: true },
  { key: "datetime_validated", label: "Date Validated", sortable: true },
  { key: "scanned_by", label: "Scanned By", sortable: true },
  { key: "full_name", label: "Received By", sortable: true },
  { key: "date_uploaded", label: "Date Uploaded", sortable: true },
  { key: "_filen", label: "Filename", sortable: true },
  { key: "status", label: "Status", sortable: true },
];

const DEFAULT_SORT_BY = "claim_processed_date";
const DEFAULT_SORT_DIR = "asc";

const getAuthHeaders = () => {
  const token = localStorage.getItem("authentication/token");
  return { Authorization: `Bearer ${token}` };
};

const getPageNumbers = (current, total) => {
  const visible = 5;
  if (total <= visible) {
    return Array.from({ length: total }, (_, idx) => idx + 1);
  }
  const start = Math.max(1, current - Math.floor(visible / 2));
  const end = Math.min(total, start + visible - 1);
  const adjustedStart = Math.max(1, end - visible + 1);
  return Array.from(
    { length: end - adjustedStart + 1 },
    (_, idx) => adjustedStart + idx
  );
};

const ReceivedScannedDataTable = () => {
  const [filterType, setFilterType] = useState("rec");
  const [uploader, setUploader] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [uploaderOptions, setUploaderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortDir, setSortDir] = useState(DEFAULT_SORT_DIR);
  const [isExporting, setIsExporting] = useState(false);

  const pageNumbers = useMemo(
    () => getPageNumbers(page, totalPages),
    [page, totalPages]
  );
  const skeletonRows = useMemo(
    () => Array.from({ length: Math.min(perPage, 10) }),
    [perPage]
  );
  const phpFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const fetchUploaders = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/received-scanned/scanners`,
        { headers: getAuthHeaders() }
      );
      const payload = response.data?.data ?? response.data ?? [];
      setUploaderOptions(payload);
    } catch (err) {
      setUploaderOptions([]);
    }
  };

  const fetchRows = async (override = {}) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/claims/received-scanned`, {
        headers: getAuthHeaders(),
        params: {
          type: filterType,
          start_date: dateFrom || undefined,
          end_date: dateTo || undefined,
          user: uploader,
          search: searchTerm || undefined,
          page: override.page ?? page,
          per_page: perPage,
          sort_by: sortBy || DEFAULT_SORT_BY,
          sort_dir: sortDir || DEFAULT_SORT_DIR,
        },
      });
      const payload = response.data?.data ?? response.data ?? {};
      setRows(payload.rows || []);
      setTotalPages(payload.total_pages || 1);
      setTotalRows(payload.total || 0);
    } catch (err) {
      setRows([]);
      setTotalPages(1);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const to = today.toISOString().split("T")[0];
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    setDateFrom(from.toISOString().split("T")[0]);
    setDateTo(to);
    fetchUploaders();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (page !== 1) {
        setPage(1);
        return;
      }
      fetchRows({ page: 1 });
    }, 350);
    return () => clearTimeout(timeout);
  }, [
    filterType,
    uploader,
    dateFrom,
    dateTo,
    searchTerm,
    sortBy,
    sortDir,
    perPage,
  ]);

  useEffect(() => {
    fetchRows();
  }, [page]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const getSortIndicator = (key) => {
    if (sortBy !== key) return "";
    return sortDir === "asc" ? "^" : "v";
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    return phpFormatter.format(numeric);
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString().split("T")[0];
  };

  const resolveUploaderId = (option) => option.id ?? option.tbl_id ?? "";

  const statusBadge = (value) => {
    const base =
      "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold";
    if (!value) return `${base} bg-slate-100 text-slate-500`;
    const normalized = String(value).toLowerCase();
    if (normalized === "paid") return `${base} bg-emerald-100 text-emerald-700`;
    if (normalized === "processed") return `${base} bg-blue-100 text-blue-700`;
    if (normalized === "for processing")
      return `${base} bg-amber-100 text-amber-700`;
    if (normalized === "extracted")
      return `${base} bg-indigo-100 text-indigo-700`;
    if (normalized === "incomplete") return `${base} bg-red-100 text-red-700`;
    return `${base} bg-slate-100 text-slate-700`;
  };

  const renderCell = (col, row) => {
    const value = row[col.key];
    if (col.key === "claim_amount") return formatAmount(value);
    if (
      [
        "date_received",
        "date_scanned_a",
        "claim_processed_date",
        "datetime_validated",
        "date_uploaded",
        "loa_date_issued",
      ].includes(col.key)
    ) {
      return formatDate(value);
    }
    if (col.key === "status") {
      return <span className={statusBadge(value)}>{value || "N/A"}</span>;
    }
    return value ?? "-";
  };

  const handleResetFilters = () => {
    const today = new Date();
    const to = today.toISOString().split("T")[0];
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    setFilterType("rec");
    setUploader("");
    setDateFrom(from.toISOString().split("T")[0]);
    setDateTo(to);
    setSearchTerm("");
    setSortBy(DEFAULT_SORT_BY);
    setSortDir(DEFAULT_SORT_DIR);
    setPerPage(10);
    setPage(1);
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/received-scanned/export`,
        {
          headers: getAuthHeaders(),
          responseType: "blob",
          params: {
            format,
            type: filterType,
            start_date: dateFrom || undefined,
            end_date: dateTo || undefined,
            user: uploader,
            search: searchTerm || undefined,
            sort_by: sortBy || DEFAULT_SORT_BY,
            sort_dir: sortDir || DEFAULT_SORT_DIR,
          },
        }
      );

      const extension = format === "excel" ? "xls" : "csv";
      const blob = new Blob([response.data], {
        type:
          format === "excel" ? "application/vnd.ms-excel" : "text/csv",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `received_scanned_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // ignore
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="mt-1 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">
            Records
          </p>
          <h3 className="text-lg font-semibold text-slate-900">
            Received & Scanned Logs
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleExport("csv")}
            disabled={isExporting}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faDownload} />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport("excel")}
            disabled={isExporting}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faDownload} />
            Export Excel
          </button>
          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faRotateLeft} />
            Reset
          </button>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-500">
          <FontAwesomeIcon icon={faFilter} />
          Filters
        </div>
        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-3">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Filter by
            </label>
            <select
              value={filterType}
              onChange={(event) => setFilterType(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Uploader
            </label>
            <select
              value={uploader}
              onChange={(event) => setUploader(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {uploaderOptions.map((option) => (
                <option
                  key={resolveUploaderId(option)}
                  value={resolveUploaderId(option)}
                >
                  {option.full_name || option.username}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Date From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Date To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Search
            </label>
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search"
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
        <span>
          {loading
            ? "Loading records..."
            : `Showing ${rows.length} of ${totalRows} records`}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs uppercase text-slate-400">Display</label>
          <select
            value={perPage}
            onChange={(event) => {
              setPerPage(Number(event.target.value));
              setPage(1);
            }}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600"
          >
            {[5, 10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
          <span>
            Page {page} of {totalPages}
          </span>
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-100/80 text-xs uppercase text-slate-500 sticky top-0 z-10 backdrop-blur">
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 ${
                    col.sortable ? "cursor-pointer select-none" : ""
                  }`}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable && (
                      <span className="text-[10px] text-slate-400">
                        {getSortIndicator(col.key)}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              skeletonRows.map((_, rowIdx) => (
                <tr key={`skeleton-${rowIdx}`} className="animate-pulse">
                  {TABLE_COLUMNS.map((col) => (
                    <td key={`${rowIdx}-${col.key}`} className="px-4 py-3">
                      <div className="h-3 w-full max-w-[160px] rounded-full bg-slate-200/80" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={TABLE_COLUMNS.length}
                  className="px-4 py-6 text-center text-sm text-slate-500"
                >
                  No records found.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.tbl_id || idx}
                  className="hover:bg-slate-50 transition-colors"
                >
                  {TABLE_COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-3 text-slate-700 whitespace-nowrap"
                    >
                      {renderCell(col, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2 text-xs">
        <button
          type="button"
          onClick={() => setPage(1)}
          disabled={page === 1}
          className="rounded-md border border-slate-200 px-3 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          First
        </button>
        <button
          type="button"
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={page === 1}
          className="rounded-md border border-slate-200 px-3 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          Previous
        </button>
        {pageNumbers.map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => setPage(num)}
            className={`rounded-md border px-3 py-1 ${
              num === page
                ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {num}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={page === totalPages}
          className="rounded-md border border-slate-200 px-3 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          Next
        </button>
        <button
          type="button"
          onClick={() => setPage(totalPages)}
          disabled={page === totalPages}
          className="rounded-md border border-slate-200 px-3 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          Last
        </button>
      </div>
    </div>
  );
};

export default ReceivedScannedDataTable;




